import type { AddressTier } from "@solomon-labs/design/components/ui";
import type React from "react";
import { useLayoutEffect, useState } from "react";

/** Characters kept when the box is too narrow to fit even a short head/tail. */
const MIN_VISIBLE_CHARS = 9;
/** Glyphs of slack held back from the fit: the face is proportional, so the
 *  per-glyph width is an average, not exact. */
const FIT_SLACK_CHARS = 2;
/** The middle-truncation separator, as the DS `Address` wide rungs use. */
const SEPARATOR = "...";

const FULL: AddressTier = { minWidth: 0 };

const NO_RESERVE: ReadonlyArray<React.RefObject<HTMLElement | null>> = [];

/**
 * The one-rung `Address` ladder for an address in a box of `boxWidth`: the
 * full address when it fits, otherwise the widest middle truncation that
 * does. The DS `Address` picks rungs by fixed breakpoints; where the room an
 * address gets depends on its neighbours (a label before it, a sentence
 * around it), the fit is decided against the measured box and handed over as
 * a single rung at `minWidth: 0`.
 */
export function fitAddressTier(length: number, fullWidth: number, boxWidth: number): AddressTier {
    // Unmeasured (not laid out, hidden, jsdom): nothing to shorten for.
    if (!(fullWidth > 0) || !(boxWidth > 0)) return FULL;
    if (fullWidth <= boxWidth || length === 0) return FULL;
    const perChar = fullWidth / length;
    const chars = Math.max(MIN_VISIBLE_CHARS, Math.floor(boxWidth / perChar) - FIT_SLACK_CHARS);
    const visible = chars - SEPARATOR.length;
    const head = Math.ceil(visible / 2);
    return { minWidth: 0, head, tail: visible - head, separator: SEPARATOR };
}

/** The DS `Address` default ladder's shortened rungs, widest first. */
const DS_RUNGS: AddressTier[] = [
    { minWidth: 0, head: 8, tail: 8, separator: "..." },
    { minWidth: 0, head: 4, tail: 4, separator: ".." },
    { minWidth: 0, head: 2, tail: 4, separator: ".." }
];

/**
 * Like `fitAddressTier`, but snapping to the DS `Address` rungs: the full
 * address when it fits, else the widest of 8...8, 4..4, 2..4 that does
 * (2..4 when none does). For an address inside a sentence, where the DS
 * shapes read as the address and an arbitrary cut does not.
 */
export function snapAddressTier(length: number, fullWidth: number, boxWidth: number): AddressTier {
    if (!(fullWidth > 0) || !(boxWidth > 0)) return FULL;
    if (fullWidth <= boxWidth || length === 0) return FULL;
    const perChar = fullWidth / length;
    const fits = (tier: AddressTier): boolean =>
        ((tier.head ?? 0) + (tier.tail ?? 0) + (tier.separator ?? "").length) * perChar <= boxWidth;
    return DS_RUNGS.find(fits) ?? DS_RUNGS[DS_RUNGS.length - 1];
}

/** The address as a rung renders it, the DS `Address`'s own rule: for a
 *  native input, which cannot hold the element. */
export function formatAddressTier(address: string, tier: AddressTier): string {
    const { head, tail, separator = ".." } = tier;
    if (head === undefined || tail === undefined) return address;
    if (address.length <= head + tail) return address;
    return `${address.slice(0, head)}${separator}${tail > 0 ? address.slice(-tail) : ""}`;
}

/** Content-box width: what text inside `box` can actually use. */
function contentWidth(box: HTMLElement): number {
    const style = getComputedStyle(box);
    const padding = Number.parseFloat(style.paddingLeft) + Number.parseFloat(style.paddingRight);
    return box.clientWidth - (Number.isFinite(padding) ? padding : 0);
}

/**
 * The fitted rung for `address`, re-fit whenever the box or the measure
 * resizes (viewport, a neighbour's length, a font swap).
 *
 * - `boxRef`: the element whose content width is the address's budget.
 * - `measureRef`: an unrendered (`invisible absolute whitespace-nowrap`)
 *   span holding the full address in the same face, for its natural width.
 * - `reserveRefs`: neighbours sharing the box's line (a badge, the rest of a
 *   sentence), whose widths come off the budget. Pass a stable array.
 *
 * Unmeasured (not laid out, or no `ResizeObserver`) is the full address.
 */
export function useAddressFit(
    address: string,
    boxRef: React.RefObject<HTMLElement | null>,
    measureRef: React.RefObject<HTMLElement | null>,
    enabled = true,
    reserveRefs: ReadonlyArray<React.RefObject<HTMLElement | null>> = NO_RESERVE,
    /** `snap` keeps to the DS rungs (`snapAddressTier`); `fit` cuts to the
     *  exact room (`fitAddressTier`). */
    mode: "fit" | "snap" = "fit"
): AddressTier {
    const [tier, setTier] = useState<AddressTier>(FULL);

    useLayoutEffect(() => {
        const box = boxRef.current;
        const measure = measureRef.current;
        if (!enabled || !box || !measure) {
            setTier(FULL);
            return undefined;
        }
        const reserved = (): number =>
            reserveRefs.reduce((sum, ref) => sum + (ref.current?.getBoundingClientRect().width ?? 0), 0);
        const fit = (): void => {
            const next = (mode === "snap" ? snapAddressTier : fitAddressTier)(
                address.length,
                measure.getBoundingClientRect().width,
                contentWidth(box) - reserved()
            );
            setTier((current) => (current.head === next.head && current.tail === next.tail ? current : next));
        };
        fit();
        if (typeof ResizeObserver === "undefined") return undefined;
        const observer = new ResizeObserver(fit);
        observer.observe(box);
        observer.observe(measure);
        for (const ref of reserveRefs) {
            if (ref.current) observer.observe(ref.current);
        }
        return (): void => observer.disconnect();
    }, [address, boxRef, measureRef, enabled, reserveRefs, mode]);

    return tier;
}
